"use client";

import React, { useEffect, useMemo, useState } from 'react';
import Breadcrumb from './ui/Breadcrumb';
import { useTranslations } from 'next-intl';
import { AssignmentIcon } from './icons';
import styles from './StaticPage.module.css';

// Anchor ids are structural, not copy: they live in code so a translated
// `sections.N.id` can never desync the TOC links from the rendered sections.
// Order must match the `sections` array in the terms content.
const SECTION_IDS = [
    'general-provisions',
    'definitions',
    'conclusion',
    'right-of-withdrawal',
    'payments',
    'performance',
    'cashback',
    'general-provisions-usage',
    'obligations-rights',
    'consent-management',
    'aml-kyc',
    'account-suspension',
    'intellectual-property',
    'limitations',
    'limitation-of-liability',
    'force-majeure',
    'data-protection',
    'amendments',
    'validity',
    'complaints',
    'cookies',
    'miscellaneous',
] as const;

const range = (length: number) => Array.from({ length }, (_, i) => i);

const TermsAndConditions: React.FC = () => {
    const [activeSection, setActiveSection] = useState<string>(SECTION_IDS[0]);

    const tCommon = useTranslations('common');
    const t = useTranslations('terms');
    const tContact = useTranslations('privacy.sidebar');

    const breadcrumbItems = [
        { label: tCommon('breadcrumb.home'), path: '/' },
        { label: tCommon('breadcrumb.termsOfService'), path: '/terms' }
    ];

    const sections = useMemo(
        () => SECTION_IDS.map((id, i) => ({ id, title: t(`sections.${i}.title`) })),
        [t]
    );

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(sectionId);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 150;
            for (const id of SECTION_IDS) {
                const element = document.getElementById(id);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(id);
                        break;
                    }
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // A section of plain numbered clauses.
    const plainSection = (id: string, contentKey: string, count: number) => (
        <section id={id} className={styles.contentSection}>
            <h2 className={styles.sectionTitle}>{t(`content.${contentKey}.title`)}</h2>
            <ol className={styles.numberedList}>
                {range(count).map((i) => (
                    <li key={i}>{t(`content.${contentKey}.items.${i}`)}</li>
                ))}
            </ol>
        </section>
    );

    // A section whose clauses carry a bold lead-in label. Some clauses have no
    // label in the source document, so `term` is allowed to be empty and the
    // <strong> is then dropped rather than rendering a bare full stop.
    const labelledSection = (id: string, contentKey: string, count: number) => (
        <section id={id} className={styles.contentSection}>
            <h2 className={styles.sectionTitle}>{t(`content.${contentKey}.title`)}</h2>
            <ol className={styles.numberedList}>
                {range(count).map((i) => {
                    const term = t(`content.${contentKey}.items.${i}.term`);
                    return (
                        <li key={i}>
                            {term ? <><strong>{term}.</strong>{' '}</> : null}
                            {t(`content.${contentKey}.items.${i}.text`)}
                        </li>
                    );
                })}
            </ol>
        </section>
    );

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.breadcrumbBar}>
                <div className={styles.container}><Breadcrumb items={breadcrumbItems} /></div>
            </div>

            <div className={styles.heroSection}>
                <div className={styles.heroInner}>
                    <h1 className={styles.heroTitle}>{t('hero.title')}</h1>
                    <p className={styles.heroSubtitle}>{t('hero.subtitle')}</p>
                </div>
            </div>

            <div className={styles.container}>
                <div className={styles.legalLayout}>
                    <div>
                        {plainSection('general-provisions', 'generalProvisions', 4)}

                        <section id="definitions" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.definitions.title')}</h2>
                            <p className={styles.contentText}>{t('content.definitions.intro')}</p>
                            <ol className={styles.numberedList}>
                                {range(6).map((i) => (
                                    <li key={i}>
                                        <strong>{t(`content.definitions.items.${i}.term`)}</strong> {t(`content.definitions.items.${i}.definition`)}
                                    </li>
                                ))}
                            </ol>
                        </section>

                        {labelledSection('conclusion', 'conclusion', 4)}
                        {labelledSection('right-of-withdrawal', 'rightOfWithdrawal', 4)}
                        {plainSection('payments', 'payments', 3)}
                        {labelledSection('performance', 'performance', 8)}
                        {labelledSection('cashback', 'cashback', 7)}

                        <section id="general-provisions-usage" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.generalProvisionsUsage.title')}</h2>
                            <ol className={styles.numberedList}>
                                <li>
                                    {t('content.generalProvisionsUsage.intro')}
                                    <ol className={styles.letterList}>
                                        {range(6).map((i) => (
                                            <li key={i}>
                                                <strong>{t(`content.generalProvisionsUsage.usageTerms.${i}.term`)}.</strong> {t(`content.generalProvisionsUsage.usageTerms.${i}.text`)}
                                            </li>
                                        ))}
                                    </ol>
                                </li>
                                <li>
                                    <strong>{t('content.generalProvisionsUsage.prohibitedUse.term')}.</strong> {t('content.generalProvisionsUsage.prohibitedUse.intro')}
                                    <ol className={styles.letterList}>
                                        {range(4).map((i) => (
                                            <li key={i}>{t(`content.generalProvisionsUsage.prohibitedUse.items.${i}`)}</li>
                                        ))}
                                    </ol>
                                </li>
                                <li>{t('content.generalProvisionsUsage.issuerTerms')}</li>
                                <li>{t('content.generalProvisionsUsage.refusalRight')}</li>
                            </ol>
                        </section>

                        <section id="obligations-rights" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.obligationsRights.title')}</h2>
                            <h3 className={styles.subSectionTitle}>{t('content.obligationsRights.yourObligationsTitle')}</h3>
                            <ol className={styles.numberedList}>
                                {range(6).map((i) => (
                                    <li key={i}>{t(`content.obligationsRights.yourObligations.${i}`)}</li>
                                ))}
                            </ol>
                            <h3 className={styles.subSectionTitle}>{t('content.obligationsRights.yourRightsTitle')}</h3>
                            <h3 className={styles.subSectionTitle}>{t('content.obligationsRights.ourRightsTitle')}</h3>
                        </section>

                        {labelledSection('consent-management', 'consentManagement', 5)}
                        {labelledSection('aml-kyc', 'amlKyc', 3)}
                        {plainSection('account-suspension', 'accountSuspension', 1)}
                        {plainSection('intellectual-property', 'intellectualProperty', 1)}
                        {plainSection('limitations', 'limitations', 5)}
                        {plainSection('limitation-of-liability', 'limitationOfLiability', 1)}
                        {plainSection('force-majeure', 'forceMajeure', 1)}
                        {plainSection('data-protection', 'dataProtection', 1)}
                        {plainSection('amendments', 'amendments', 1)}
                        {plainSection('validity', 'validity', 1)}
                        {plainSection('complaints', 'complaints', 4)}
                        {plainSection('cookies', 'cookies', 5)}

                        <section id="miscellaneous" className={styles.contentSection}>
                            <h2 className={styles.sectionTitle}>{t('content.miscellaneous.title')}</h2>
                            <ol className={styles.numberedList}>
                                {range(6).map((i) => (
                                    <li key={i}>
                                        <strong>{t(`content.miscellaneous.items.${i}.term`)}.</strong> {t(`content.miscellaneous.items.${i}.text`)}
                                    </li>
                                ))}
                            </ol>
                        </section>

                        <div className={styles.lastUpdated}>{t('lastUpdated')}</div>
                    </div>

                    <div>
                        <div className={styles.tocSidebar}>
                            <h3 className={styles.tocTitle}>
                                <AssignmentIcon fontSize="small" />
                                {t('toc.title')}
                            </h3>

                            <nav aria-label={t('toc.title')}>
                                <ul className={styles.tocList}>
                                    {sections.map((section) => (
                                        <li
                                            key={section.id}
                                            className={`${styles.tocItem} ${activeSection === section.id ? styles.tocItemActive : ''}`}
                                            onClick={() => scrollToSection(section.id)}
                                        >
                                            {section.title}
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <hr className={styles.tocDivider} />

                            <div className={styles.sidebarContactInfo}>
                                <p><strong>{tContact('contactTitle')}</strong></p>
                                <p>{tContact('company')}</p>
                                <p>{tContact('registration')}</p>
                                <p><a href={`mailto:${tContact('email')}`}>{tContact('email')}</a></p>
                            </div>

                            <div className={styles.sidebarNeedHelp}>
                                <h4>{tContact('needHelpTitle')}</h4>
                                <p><a href={`mailto:${tContact('email')}`}>{tContact('email')}</a></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsAndConditions;
